/*!
 * devextreme-angular
 * Version: 21.2.4
 * Build date: Mon Dec 06 2021
 *
 * Copyright (c) 2012 - 2021 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */


import { BrowserTransferStateModule } from '@angular/platform-browser';
import { TransferState } from '@angular/platform-browser';

import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';
import Store from 'devextreme/data/abstract_store';
import DataSource, { Options as DataSourceOptions } from 'devextreme/data/data_source';
import { dxGanttContextMenu, dxGanttFilterRow, dxGanttHeaderFilter, dxGanttSorting, dxGanttToolbar } from 'devextreme/ui/gantt';

import DxGantt from 'devextreme/ui/gantt';


import {
    DxComponent,
    DxTemplateHost,
    DxIntegrationModule,
    DxTemplateModule,
    NestedOptionHost,
    IterableDifferHelper,
    WatcherHelper
} from 'devextreme-angular/core';

import { DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxiButtonModule } from 'devextreme-angular/ui/nested';
import { DxoHeaderFilterModule } from 'devextreme-angular/ui/nested';
import { DxoLookupModule } from 'devextreme-angular/ui/nested';
import { DxoFormatModule } from 'devextreme-angular/ui/nested';
import { DxoFormItemModule } from 'devextreme-angular/ui/nested';
import { DxoLabelModule } from 'devextreme-angular/ui/nested';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { DxoContextMenuModule } from 'devextreme-angular/ui/nested';
import { DxiItemModule } from 'devextreme-angular/ui/nested';
import { DxoDependenciesModule } from 'devextreme-angular/ui/nested';
import { DxoEditingModule } from 'devextreme-angular/ui/nested';
import { DxoFilterRowModule } from 'devextreme-angular/ui/nested';
import { DxoOperationDescriptionsModule } from 'devextreme-angular/ui/nested';
import { DxoTextsModule } from 'devextreme-angular/ui/nested';
import { DxoResourceAssignmentsModule } from 'devextreme-angular/ui/nested';
import { DxoResourcesModule } from 'devextreme-angular/ui/nested';
import { DxoScaleTypeRangeModule } from 'devextreme-angular/ui/nested';
import { DxoSortingModule } from 'devextreme-angular/ui/nested';
import { DxiStripLineModule } from 'devextreme-angular/ui/nested';
import { DxoTasksModule } from 'devextreme-angular/ui/nested';
import { DxoToolbarModule } from 'devextreme-angular/ui/nested';
import { DxoValidationModule } from 'devextreme-angular/ui/nested';

import { DxiColumnComponent } from 'devextreme-angular/ui/nested';
import { DxiStripLineComponent } from 'devextreme-angular/ui/nested';



/**
 * The Gantt is a UI component that displays the task flow and dependencies between tasks.

 */
@Component({
    selector: 'dx-gantt',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxGanttComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxGantt;

    /**
     * Specifies the shortcut key that sets focus on the UI component.
    
     */
    @Input()
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether or not the UI component changes its state when interacting with a user.
    
     */
    @Input()
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies whether users can select tasks in the Gantt.
    
     */
    @Input()
    get allowSelection(): boolean {
        return this._getOption('allowSelection');
    }
    set allowSelection(value: boolean) {
        this._setOption('allowSelection', value);
    }


    /**
     * An array of columns in the Gantt.
    
     */
    @Input()
    get columns(): Array<DevExpress.ui.dxTreeListColumn | string> {
        return this._getOption('columns');
    }
    set columns(value: Array<DevExpress.ui.dxTreeListColumn | string>) {
        this._setOption('columns', value);
    }


    /**
     * Configures the context menu settings.
    
     */
    @Input()
    get contextMenu(): dxGanttContextMenu {
        return this._getOption('contextMenu');
    }
    set contextMenu(value: dxGanttContextMenu) {
        this._setOption('contextMenu', value);
    }


    /**
     * Configures dependencies.
    
     */
    @Input()
    get dependencies(): { dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, predecessorIdExpr?: Function | string, successorIdExpr?: Function | string, typeExpr?: Function | string } {
        return this._getOption('dependencies');
    }
    set dependencies(value: { dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, predecessorIdExpr?: Function | string, successorIdExpr?: Function | string, typeExpr?: Function | string }) {
        this._setOption('dependencies', value);
    }


    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Configures edit properties.
    
     */
    @Input()
    get editing(): { allowDependencyAdding?: boolean, allowDependencyDeleting?: boolean, allowResourceAdding?: boolean, allowResourceDeleting?: boolean, allowResourceUpdating?: boolean, allowTaskAdding?: boolean, allowTaskDeleting?: boolean, allowTaskResourceUpdating?: boolean, allowTaskUpdating?: boolean, enabled?: boolean } {
        return this._getOption('editing');
    }
    set editing(value: { allowDependencyAdding?: boolean, allowDependencyDeleting?: boolean, allowResourceAdding?: boolean, allowResourceDeleting?: boolean, allowResourceUpdating?: boolean, allowTaskAdding?: boolean, allowTaskDeleting?: boolean, allowTaskResourceUpdating?: boolean, allowTaskUpdating?: boolean, enabled?: boolean }) {
        this._setOption('editing', value);
    }


    /**
     * Specifies the global attributes to be attached to the UI component&apos;s container element.
    
     */
    @Input()
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the end date of the date interval in the Gantt chart.
    
     */
    @Input()
    get endDateRange(): Date {
        return this._getOption('endDateRange');
    }
    set endDateRange(value: Date) {
        this._setOption('endDateRange', value);
    }


    /**
     * Configures filter row settings.
    
     */
    @Input()
    get filterRow(): dxGanttFilterRow {
        return this._getOption('filterRow');
    }
    set filterRow(value: dxGanttFilterRow) {
        this._setOption('filterRow', value);
    }


    /**
     * Specifies the first day of a week.
    
     */
    @Input()
    get firstDayOfWeek(): number | string {
        return this._getOption('firstDayOfWeek');
    }
    set firstDayOfWeek(value: number | string) {
        this._setOption('firstDayOfWeek', value);
    }


    /**
     * Specifies whether the UI component can be focused using keyboard navigation.
    
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Configures the header filter settings.
    
     */
    @Input()
    get headerFilter(): dxGanttHeaderFilter {
        return this._getOption('headerFilter');
    }
    set headerFilter(value: dxGanttHeaderFilter) {
        this._setOption('headerFilter', value);
    }


    /**
     * Specifies the UI component&apos;s height.
    
     */
    @Input()
    get height(): number | Function | string {
        return this._getOption('height');
    }
    set height(value: number | Function | string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    @Input()
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the UI component changes its state when a user pauses on it.
    
     */
    @Input()
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Configures resource assignments.
    
     */
    @Input()
    get resourceAssignments(): { dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, resourceIdExpr?: Function | string, taskIdExpr?: Function | string } {
        return this._getOption('resourceAssignments');
    }
    set resourceAssignments(value: { dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, resourceIdExpr?: Function | string, taskIdExpr?: Function | string }) {
        this._setOption('resourceAssignments', value);
    }


    /**
     * Configures task resources.
    
     */
    @Input()
    get resources(): { colorExpr?: Function | string, dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, textExpr?: Function | string } {
        return this._getOption('resources');
    }
    set resources(value: { colorExpr?: Function | string, dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, textExpr?: Function | string }) {
        this._setOption('resources', value);
    }


    /**
     * Specifies the root task&apos;s identifier.
    
     */
    @Input()
    get rootValue(): any {
        return this._getOption('rootValue');
    }
    set rootValue(value: any) {
        this._setOption('rootValue', value);
    }


    /**
     * Specifies the zoom level of tasks in the Gantt chart.
    
     */
    @Input()
    get scaleType(): string {
        return this._getOption('scaleType');
    }
    set scaleType(value: string) {
        this._setOption('scaleType', value);
    }


    /**
     * Configures zoom range settings.
    
     */
    @Input()
    get scaleTypeRange(): { max?: string, min?: string } {
        return this._getOption('scaleTypeRange');
    }
    set scaleTypeRange(value: { max?: string, min?: string }) {
        this._setOption('scaleTypeRange', value);
    }


    /**
     * Allows you to select a row or determine which row is selected.
    
     */
    @Input()
    get selectedRowKey(): any {
        return this._getOption('selectedRowKey');
    }
    set selectedRowKey(value: any) {
        this._setOption('selectedRowKey', value);
    }


    /**
     * Specifies whether to display dependencies between tasks.
    
     */
    @Input()
    get showDependencies(): boolean {
        return this._getOption('showDependencies');
    }
    set showDependencies(value: boolean) {
        this._setOption('showDependencies', value);
    }


    /**
     * Specifies whether to display task resources.
    
     */
    @Input()
    get showResources(): boolean {
        return this._getOption('showResources');
    }
    set showResources(value: boolean) {
        this._setOption('showResources', value);
    }


    /**
     * Specifies whether to show/hide horizontal faint lines that separate tasks.
    
     */
    @Input()
    get showRowLines(): boolean {
        return this._getOption('showRowLines');
    }
    set showRowLines(value: boolean) {
        this._setOption('showRowLines', value);
    }


    /**
     * Configures sort settings.
    
     */
    @Input()
    get sorting(): dxGanttSorting {
        return this._getOption('sorting');
    }
    set sorting(value: dxGanttSorting) {
        this._setOption('sorting', value);
    }


    /**
     * Specifies the start date of the date interval in the Gantt chart.
    
     */
    @Input()
    get startDateRange(): Date {
        return this._getOption('startDateRange');
    }
    set startDateRange(value: Date) {
        this._setOption('startDateRange', value);
    }


    /**
     * Configures strip lines.
    
     */
    @Input()
    get stripLines(): Array<DevExpress.ui.dxGanttStripLine> {
        return this._getOption('stripLines');
    }
    set stripLines(value: Array<DevExpress.ui.dxGanttStripLine>) {
        this._setOption('stripLines', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
    
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies custom content for the task.
    
     */
    @Input()
    get taskContentTemplate(): any {
        return this._getOption('taskContentTemplate');
    }
    set taskContentTemplate(value: any) {
        this._setOption('taskContentTemplate', value);
    }


    /**
     * Specifies the width of the task list in pixels.
    
     */
    @Input()
    get taskListWidth(): number {
        return this._getOption('taskListWidth');
    }
    set taskListWidth(value: number) {
        this._setOption('taskListWidth', value);
    }


    /**
     * Specifies custom content for the tooltip that displays the task&apos;s progress while the progress handler is resized in the UI.
    
     */
    @Input()
    get taskProgressTooltipContentTemplate(): any {
        return this._getOption('taskProgressTooltipContentTemplate');
    }
    set taskProgressTooltipContentTemplate(value: any) {
        this._setOption('taskProgressTooltipContentTemplate', value);
    }


    /**
     * Configures tasks.
    
     */
    @Input()
    get tasks(): { colorExpr?: Function | string, dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, endExpr?: Function | string, keyExpr?: Function | string, parentIdExpr?: Function | string, progressExpr?: Function | string, startExpr?: Function | string, titleExpr?: Function | string } {
        return this._getOption('tasks');
    }
    set tasks(value: { colorExpr?: Function | string, dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, endExpr?: Function | string, keyExpr?: Function | string, parentIdExpr?: Function | string, progressExpr?: Function | string, startExpr?: Function | string, titleExpr?: Function | string }) {
        this._setOption('tasks', value);
    }


    /**
     * Specifies custom content for the tooltip that displays the task&apos;s start and end time while the task is resized in the UI.
    
     */
    @Input()
    get taskTimeTooltipContentTemplate(): any {
        return this._getOption('taskTimeTooltipContentTemplate');
    }
    set taskTimeTooltipContentTemplate(value: any) {
        this._setOption('taskTimeTooltipContentTemplate', value);
    }


    /**
     * Specifies a task&apos;s title position.
    
     */
    @Input()
    get taskTitlePosition(): string {
        return this._getOption('taskTitlePosition');
    }
    set taskTitlePosition(value: string) {
        this._setOption('taskTitlePosition', value);
    }


    /**
     * Specifies custom content for the task tooltip.
    
     */
    @Input()
    get taskTooltipContentTemplate(): any {
        return this._getOption('taskTooltipContentTemplate');
    }
    set taskTooltipContentTemplate(value: any) {
        this._setOption('taskTooltipContentTemplate', value);
    }


    /**
     * Configures toolbar settings.
    
     */
    @Input()
    get toolbar(): dxGanttToolbar {
        return this._getOption('toolbar');
    }
    set toolbar(value: dxGanttToolbar) {
        this._setOption('toolbar', value);
    }


    /**
     * Configures validation properties.
    
     */
    @Input()
    get validation(): { autoUpdateParentTasks?: boolean, enablePredecessorGap?: boolean, validateDependencies?: boolean } {
        return this._getOption('validation');
    }
    set validation(value: { autoUpdateParentTasks?: boolean, enablePredecessorGap?: boolean, validateDependencies?: boolean }) {
        this._setOption('validation', value);
    }


    /**
     * Specifies whether the UI component is visible.
    
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the UI component&apos;s width.
    
     */
    @Input()
    get width(): number | Function | string {
        return this._getOption('width');
    }
    set width(value: number | Function | string) {
        this._setOption('width', value);
    }

    /**
    
     * A function that is executed when the UI component&apos;s content is ready and each time the content is changed.
    
    
     */
    @Output() onContentReady: EventEmitter<any>;

    /**
    
     * A function that is executed before the context menu is rendered.
    
    
     */
    @Output() onContextMenuPreparing: EventEmitter<any>;

    /**
    
     * A function that is executed after a custom command item was clicked. Allows you to implement a custom command&apos;s functionality.
    
    
     */
    @Output() onCustomCommand: EventEmitter<any>;

    /**
    
     * A function that is executed when a dependency is deleted.
    
    
     */
    @Output() onDependencyDeleted: EventEmitter<any>;

    /**
    
     * A function that is executed before a dependency is deleted.
    
    
     */
    @Output() onDependencyDeleting: EventEmitter<any>;

    /**
    
     * A function that is executed when a dependency is inserted.
    
    
     */
    @Output() onDependencyInserted: EventEmitter<any>;

    /**
    
     * A function that is executed before a dependency is inserted.
    
    
     */
    @Output() onDependencyInserting: EventEmitter<any>;

    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    @Output() onDisposing: EventEmitter<any>;

    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    @Output() onInitialized: EventEmitter<any>;

    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    @Output() onOptionChanged: EventEmitter<any>;

    /**
    
     * A function that is executed when a resource is assigned to a task.
    
    
     */
    @Output() onResourceAssigned: EventEmitter<any>;

    /**
    
     * A function that is executed before a resource is assigned to a task.
    
    
     */
    @Output() onResourceAssigning: EventEmitter<any>;

    /**
    
     * A function that is executed when a resource is deleted.
    
    
     */
    @Output() onResourceDeleted: EventEmitter<any>;

    /**
    
     * A function that is executed before a resource is deleted.
    
    
     */
    @Output() onResourceDeleting: EventEmitter<any>;

    /**
    
     * A function that is executed when a resource is inserted.
    
    
     */
    @Output() onResourceInserted: EventEmitter<any>;

    /**
    
     * A function that is executed before a resource is inserted.
    
    
     */
    @Output() onResourceInserting: EventEmitter<any>;

    /**
    
     * A function that is executed before the Resource Manager dialog is shown.
    
    
     */
    @Output() onResourceManagerDialogShowing: EventEmitter<any>;

    /**
    
     * A function that is executed when a resource is unassigned from a task.
    
    
     */
    @Output() onResourceUnassigned: EventEmitter<any>;

    /**
    
     * A function that is executed before a resource is unassigned from a task.
    
    
     */
    @Output() onResourceUnassigning: EventEmitter<any>;

    /**
    
     * A function that is executed after users select a task or clear its selection.
    
    
     */
    @Output() onSelectionChanged: EventEmitter<any>;

    /**
    
     * A function that is executed when a user clicks a task.
    
    
     */
    @Output() onTaskClick: EventEmitter<any>;

    /**
    
     * A function that is executed when a user double-clicks a task.
    
    
     */
    @Output() onTaskDblClick: EventEmitter<any>;

    /**
    
     * A function that is executed when a task is deleted.
    
    
     */
    @Output() onTaskDeleted: EventEmitter<any>;

    /**
    
     * A function that is executed before a task is deleted.
    
    
     */
    @Output() onTaskDeleting: EventEmitter<any>;

    /**
    
     * A function that is executed before the edit dialog is shown.
    
    
     */
    @Output() onTaskEditDialogShowing: EventEmitter<any>;

    /**
    
     * A function that is executed when a task is inserted.
    
    
     */
    @Output() onTaskInserted: EventEmitter<any>;

    /**
    
     * A function that is executed before a task is inserted.
    
    
     */
    @Output() onTaskInserting: EventEmitter<any>;

    /**
    
     * A function that is executed before a task is moved.
    
    
     */
    @Output() onTaskMoving: EventEmitter<any>;

    /**
    
     * A function that is executed when a task is updated.
    
    
     */
    @Output() onTaskUpdated: EventEmitter<any>;

    /**
    
     * A function that is executed before a task is updated.
    
    
     */
    @Output() onTaskUpdating: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() accessKeyChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() activeStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() allowSelectionChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() columnsChange: EventEmitter<Array<DevExpress.ui.dxTreeListColumn | string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() contextMenuChange: EventEmitter<dxGanttContextMenu>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() dependenciesChange: EventEmitter<{ dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, predecessorIdExpr?: Function | string, successorIdExpr?: Function | string, typeExpr?: Function | string }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() editingChange: EventEmitter<{ allowDependencyAdding?: boolean, allowDependencyDeleting?: boolean, allowResourceAdding?: boolean, allowResourceDeleting?: boolean, allowResourceUpdating?: boolean, allowTaskAdding?: boolean, allowTaskDeleting?: boolean, allowTaskResourceUpdating?: boolean, allowTaskUpdating?: boolean, enabled?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() elementAttrChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() endDateRangeChange: EventEmitter<Date>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() filterRowChange: EventEmitter<dxGanttFilterRow>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() firstDayOfWeekChange: EventEmitter<number | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() headerFilterChange: EventEmitter<dxGanttHeaderFilter>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() heightChange: EventEmitter<number | Function | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hintChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hoverStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() resourceAssignmentsChange: EventEmitter<{ dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, resourceIdExpr?: Function | string, taskIdExpr?: Function | string }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() resourcesChange: EventEmitter<{ colorExpr?: Function | string, dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, keyExpr?: Function | string, textExpr?: Function | string }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rootValueChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() scaleTypeChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() scaleTypeRangeChange: EventEmitter<{ max?: string, min?: string }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectedRowKeyChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showDependenciesChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showResourcesChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showRowLinesChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() sortingChange: EventEmitter<dxGanttSorting>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() startDateRangeChange: EventEmitter<Date>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() stripLinesChange: EventEmitter<Array<DevExpress.ui.dxGanttStripLine>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() taskContentTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() taskListWidthChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() taskProgressTooltipContentTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() tasksChange: EventEmitter<{ colorExpr?: Function | string, dataSource?: DataSource | DataSourceOptions | Store | string | Array<any>, endExpr?: Function | string, keyExpr?: Function | string, parentIdExpr?: Function | string, progressExpr?: Function | string, startExpr?: Function | string, titleExpr?: Function | string }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() taskTimeTooltipContentTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() taskTitlePositionChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() taskTooltipContentTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() toolbarChange: EventEmitter<dxGanttToolbar>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() validationChange: EventEmitter<{ autoUpdateParentTasks?: boolean, enablePredecessorGap?: boolean, validateDependencies?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() widthChange: EventEmitter<number | Function | string>;




    @ContentChildren(DxiColumnComponent)
    get columnsChildren(): QueryList<DxiColumnComponent> {
        return this._getOption('columns');
    }
    set columnsChildren(value) {
        this.setChildren('columns', value);
    }

    @ContentChildren(DxiStripLineComponent)
    get stripLinesChildren(): QueryList<DxiStripLineComponent> {
        return this._getOption('stripLines');
    }
    set stripLinesChildren(value) {
        this.setChildren('stripLines', value);
    }




    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper,
            optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'contextMenuPreparing', emit: 'onContextMenuPreparing' },
            { subscribe: 'customCommand', emit: 'onCustomCommand' },
            { subscribe: 'dependencyDeleted', emit: 'onDependencyDeleted' },
            { subscribe: 'dependencyDeleting', emit: 'onDependencyDeleting' },
            { subscribe: 'dependencyInserted', emit: 'onDependencyInserted' },
            { subscribe: 'dependencyInserting', emit: 'onDependencyInserting' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'resourceAssigned', emit: 'onResourceAssigned' },
            { subscribe: 'resourceAssigning', emit: 'onResourceAssigning' },
            { subscribe: 'resourceDeleted', emit: 'onResourceDeleted' },
            { subscribe: 'resourceDeleting', emit: 'onResourceDeleting' },
            { subscribe: 'resourceInserted', emit: 'onResourceInserted' },
            { subscribe: 'resourceInserting', emit: 'onResourceInserting' },
            { subscribe: 'resourceManagerDialogShowing', emit: 'onResourceManagerDialogShowing' },
            { subscribe: 'resourceUnassigned', emit: 'onResourceUnassigned' },
            { subscribe: 'resourceUnassigning', emit: 'onResourceUnassigning' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { subscribe: 'taskClick', emit: 'onTaskClick' },
            { subscribe: 'taskDblClick', emit: 'onTaskDblClick' },
            { subscribe: 'taskDeleted', emit: 'onTaskDeleted' },
            { subscribe: 'taskDeleting', emit: 'onTaskDeleting' },
            { subscribe: 'taskEditDialogShowing', emit: 'onTaskEditDialogShowing' },
            { subscribe: 'taskInserted', emit: 'onTaskInserted' },
            { subscribe: 'taskInserting', emit: 'onTaskInserting' },
            { subscribe: 'taskMoving', emit: 'onTaskMoving' },
            { subscribe: 'taskUpdated', emit: 'onTaskUpdated' },
            { subscribe: 'taskUpdating', emit: 'onTaskUpdating' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'allowSelectionChange' },
            { emit: 'columnsChange' },
            { emit: 'contextMenuChange' },
            { emit: 'dependenciesChange' },
            { emit: 'disabledChange' },
            { emit: 'editingChange' },
            { emit: 'elementAttrChange' },
            { emit: 'endDateRangeChange' },
            { emit: 'filterRowChange' },
            { emit: 'firstDayOfWeekChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'headerFilterChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'resourceAssignmentsChange' },
            { emit: 'resourcesChange' },
            { emit: 'rootValueChange' },
            { emit: 'scaleTypeChange' },
            { emit: 'scaleTypeRangeChange' },
            { emit: 'selectedRowKeyChange' },
            { emit: 'showDependenciesChange' },
            { emit: 'showResourcesChange' },
            { emit: 'showRowLinesChange' },
            { emit: 'sortingChange' },
            { emit: 'startDateRangeChange' },
            { emit: 'stripLinesChange' },
            { emit: 'tabIndexChange' },
            { emit: 'taskContentTemplateChange' },
            { emit: 'taskListWidthChange' },
            { emit: 'taskProgressTooltipContentTemplateChange' },
            { emit: 'tasksChange' },
            { emit: 'taskTimeTooltipContentTemplateChange' },
            { emit: 'taskTitlePositionChange' },
            { emit: 'taskTooltipContentTemplateChange' },
            { emit: 'toolbarChange' },
            { emit: 'validationChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {

        return new DxGantt(element, options);
    }


    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('columns', changes);
        this.setupChanges('stripLines', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('columns');
        this._idh.doCheck('stripLines');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }
}

@NgModule({
  imports: [
    DxiColumnModule,
    DxiButtonModule,
    DxoHeaderFilterModule,
    DxoLookupModule,
    DxoFormatModule,
    DxoFormItemModule,
    DxoLabelModule,
    DxiValidationRuleModule,
    DxoContextMenuModule,
    DxiItemModule,
    DxoDependenciesModule,
    DxoEditingModule,
    DxoFilterRowModule,
    DxoOperationDescriptionsModule,
    DxoTextsModule,
    DxoResourceAssignmentsModule,
    DxoResourcesModule,
    DxoScaleTypeRangeModule,
    DxoSortingModule,
    DxiStripLineModule,
    DxoTasksModule,
    DxoToolbarModule,
    DxoValidationModule,
    DxIntegrationModule,
    DxTemplateModule,
    BrowserTransferStateModule
  ],
  declarations: [
    DxGanttComponent
  ],
  exports: [
    DxGanttComponent,
    DxiColumnModule,
    DxiButtonModule,
    DxoHeaderFilterModule,
    DxoLookupModule,
    DxoFormatModule,
    DxoFormItemModule,
    DxoLabelModule,
    DxiValidationRuleModule,
    DxoContextMenuModule,
    DxiItemModule,
    DxoDependenciesModule,
    DxoEditingModule,
    DxoFilterRowModule,
    DxoOperationDescriptionsModule,
    DxoTextsModule,
    DxoResourceAssignmentsModule,
    DxoResourcesModule,
    DxoScaleTypeRangeModule,
    DxoSortingModule,
    DxiStripLineModule,
    DxoTasksModule,
    DxoToolbarModule,
    DxoValidationModule,
    DxTemplateModule
  ]
})
export class DxGanttModule { }
